import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Users, Sparkles, Inbox, RefreshCw, ChevronDown, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import Logo from "@/components/shared/Logo";
import { fetchLeads, fetchLeadStats, updateLeadStatus, fetchVisibilityChecks } from "@/lib/api";

const STATUSES = ["new", "contacted", "qualified", "won", "lost"];
const STATUS_STYLE = {
  new: "bg-[#0B63F6]/10 text-[#0B63F6]",
  contacted: "bg-[#6E56CF]/10 text-[#6E56CF]",
  qualified: "bg-amber-100 text-amber-700",
  won: "bg-[#19A974]/10 text-[#19A974]",
  lost: "bg-gray-100 text-gray-500",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [checks, setChecks] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, audits: 0 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("leads");
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [l, s, c] = await Promise.all([fetchLeads(), fetchLeadStats(), fetchVisibilityChecks()]);
      setLeads(l);
      setStats(s);
      setChecks(c);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("cb_admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Failed to load data.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("cb_admin_token")) {
      navigate("/admin/login");
      return;
    }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = () => {
    localStorage.removeItem("cb_admin_token");
    localStorage.removeItem("cb_admin_user");
    navigate("/admin/login");
  };

  const changeStatus = async (id, status) => {
    try {
      await updateLeadStatus(id, status);
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success("Status updated.");
    } catch {
      toast.error("Update failed.");
    }
  };

  const cards = [
    { icon: Inbox, label: "Total Leads", value: stats.total },
    { icon: Users, label: "New Leads", value: stats.new },
    { icon: Sparkles, label: "AI Audit Requests", value: stats.audits },
    { icon: Sparkles, label: "AI Reports Generated", value: checks.length },
  ];

  const STATUS_COLOR = { strong: "#19A974", moderate: "#0B63F6", weak: "#F36F21" };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Seo title="Admin Dashboard" description="CompuBrain AI leads." path="/admin" />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <button onClick={load} data-testid="admin-refresh" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-[#111111] transition-colors"><RefreshCw className="h-4 w-4" /> Refresh</button>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2 text-sm font-medium hover:border-[#111111] transition-colors"><LogOut className="h-4 w-4" /> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <h1 className="font-display text-3xl font-bold tracking-tight">Dashboard</h1>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-6">
          {cards.map((c) => (
            <div key={c.label} className="rounded-2xl border border-gray-200 bg-white p-6">
              <div className="flex items-center gap-3 text-gray-500"><c.icon className="h-5 w-5" /> <span className="text-sm">{c.label}</span></div>
              <div className="font-display text-4xl font-extrabold tracking-tightest mt-3">{c.value}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="mt-8 flex gap-2">
          {[{ k: "leads", label: `Leads (${leads.length})` }, { k: "reports", label: `AI Visibility Reports (${checks.length})` }].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`admin-tab-${t.k}`}
              className={`rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${tab === t.k ? "bg-[#111111] text-white" : "border border-gray-200 text-gray-600 hover:border-[#111111]"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "leads" && (
          <div className="mt-6 rounded-2xl border border-gray-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left min-w-[1000px]" data-testid="leads-table">
                <thead>
                  <tr className="text-xs font-bold uppercase tracking-[0.12em] text-gray-500 border-b border-gray-200">
                    <th className="py-4 px-6">Name</th>
                    <th className="py-4 px-6">Contact</th>
                    <th className="py-4 px-6">Company</th>
                    <th className="py-4 px-6">Website</th>
                    <th className="py-4 px-6">Interest</th>
                    <th className="py-4 px-6">Source</th>
                    <th className="py-4 px-6">Status</th>
                    <th className="py-4 px-6">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={8} className="py-16 text-center text-gray-400">Loading…</td></tr>
                  ) : leads.length === 0 ? (
                    <tr><td colSpan={8} className="py-16 text-center text-gray-400">No leads yet.</td></tr>
                  ) : (
                    leads.map((l) => (
                      <tr key={l.id} className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors" data-testid={`lead-row-${l.id}`}>
                        <td className="py-4 px-6 font-medium text-[#111111]">{l.name}</td>
                        <td className="py-4 px-6 text-sm text-gray-600">
                          <div>{l.email}</div>
                          {l.phone && <div className="text-gray-400">{l.phone}{l.phone_verified ? " ✓" : ""}</div>}
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-600">{l.company || "—"}</td>
                        <td className="py-4 px-6 text-sm text-gray-600">
                          {l.website ? <a href={l.website.startsWith("http") ? l.website : `https://${l.website}`} target="_blank" rel="noopener noreferrer" className="text-[#0B63F6] hover:underline inline-flex items-center gap-1">{l.website} <ExternalLink className="h-3 w-3" /></a> : "—"}
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-600">{l.service_interest || "—"}</td>
                        <td className="py-4 px-6 text-sm text-gray-500">{l.source}</td>
                        <td className="py-4 px-6">
                          <select value={l.status} onChange={(e) => changeStatus(l.id, e.target.value)} data-testid={`lead-status-${l.id}`} className={`rounded-full px-3 py-1.5 text-xs font-medium border-0 focus:outline-none cursor-pointer ${STATUS_STYLE[l.status] || "bg-gray-100"}`}>
                            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-500 whitespace-nowrap">{new Date(l.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "reports" && (
          <div className="mt-6 space-y-4" data-testid="reports-list">
            {loading ? (
              <div className="py-16 text-center text-gray-400">Loading…</div>
            ) : checks.length === 0 ? (
              <div className="py-16 text-center text-gray-400 rounded-2xl border border-gray-200 bg-white">No AI visibility reports yet.</div>
            ) : (
              checks.map((c) => {
                const open = expanded === c.id;
                return (
                  <div key={c.id} className="rounded-2xl border border-gray-200 bg-white overflow-hidden" data-testid={`report-row-${c.id}`}>
                    <button onClick={() => setExpanded(open ? null : c.id)} className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-gray-50/60 transition-colors">
                      <div className="flex items-center gap-4 flex-wrap">
                        <span className="font-display text-2xl font-black tracking-tightest" style={{ color: c.overall_score >= 75 ? "#19A974" : c.overall_score >= 50 ? "#0B63F6" : "#F36F21" }}>{c.overall_score}</span>
                        <div>
                          <div className="font-display font-bold">{c.brand} <span className="ml-2 text-xs rounded-full bg-[#111111] text-white px-2 py-0.5 align-middle">{c.grade || "—"}</span></div>
                          <a href={c.website?.startsWith("http") ? c.website : `https://${c.website}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-sm text-[#0B63F6] hover:underline inline-flex items-center gap-1">{c.website} <ExternalLink className="h-3 w-3" /></a>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <div className="hidden sm:block text-right">
                          {c.phone && <div>{c.phone}</div>}
                          {c.email && <div className="text-gray-400">{c.email}</div>}
                        </div>
                        <span className="whitespace-nowrap">{new Date(c.created_at).toLocaleDateString()}</span>
                        <ChevronDown className={`h-5 w-5 transition-transform ${open ? "rotate-180" : ""}`} />
                      </div>
                    </button>
                    {open && c.report && (
                      <div className="px-5 pb-6 border-t border-gray-100 pt-5">
                        <p className="text-[15px] text-[#111111] font-medium">{c.report.verdict}</p>
                        <p className="text-sm text-gray-500 mt-1">{c.report.citation_readiness}</p>
                        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 mt-5">
                          {c.report.categories?.map((cat) => (
                            <div key={cat.name}>
                              <div className="flex justify-between text-sm"><span>{cat.name}</span><span className="font-bold" style={{ color: STATUS_COLOR[cat.status] || "#0B63F6" }}>{cat.score}</span></div>
                              <div className="h-1.5 rounded-full bg-gray-100 mt-1"><div className="h-full rounded-full" style={{ width: `${cat.score}%`, backgroundColor: STATUS_COLOR[cat.status] || "#0B63F6" }} /></div>
                            </div>
                          ))}
                        </div>
                        <div className="grid sm:grid-cols-2 gap-6 mt-6">
                          <div>
                            <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#19A974] mb-2">Quick wins</div>
                            <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">{c.report.quick_wins?.map((w, i) => <li key={i}>{w}</li>)}</ul>
                          </div>
                          <div>
                            <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#6E56CF] mb-2">Strategic recommendations</div>
                            <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">{c.report.strategic_recommendations?.map((w, i) => <li key={i}>{w}</li>)}</ul>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </main>
    </div>
  );
}
